/**
 * @fileoverview Knowledge Base Assignment Module Types
 * 
 * Type definitions and validation for the Knowledge Base Assignment module.
 * This module handles assigning knowledge base files to parrots with batch operations,
 * real-time sync status tracking, and conflict resolution.
 * 
 * Requirements: 4.1, 4.2, 9.1, 9.2
 */

/**
 * @typedef {Object} KnowledgeBaseAssignmentConfig
 * @property {'user-kb-dashboard'|'parrot-creation'|'parrot-editing'|'admin-kb'} context - Context where module is used
 * @property {'single-select'|'multi-select'|'batch-assign'} mode - Assignment mode
 * @property {'user'|'admin'} securityLevel - Security level for operations
 * @property {boolean} [showSyncStatus=true] - Whether to show sync status indicators
 * @property {boolean} [allowBatchOperations=true] - Whether to allow batch operations
 * @property {boolean} [enableRealTimeUpdates=true] - Whether to enable real-time status updates
 * @property {boolean} [showConflictResolution=true] - Whether to show conflict resolution UI
 * @property {boolean} [enableAssignmentHistory=false] - Whether to track assignment history
 * @property {function(Assignment[]): void} [onAssignmentChange] - Callback when assignments change
 * @property {function(SyncStatus): void} [onSyncStatusUpdate] - Callback when sync status updates
 * @property {function(AssignmentConflict[]): void} [onConflictDetected] - Callback when conflicts detected
 * @property {function(AssignmentEvent): void} [onAssignmentEvent] - Callback for assignment events
 */

/**
 * @typedef {Object} Assignment
 * @property {string} id - Unique assignment ID
 * @property {string} fileId - Knowledge base file ID
 * @property {string} parrotId - Parrot ID
 * @property {'pending'|'assigned'|'syncing'|'synced'|'failed'|'removed'} status - Assignment status
 * @property {Date} createdAt - When assignment was created
 * @property {Date} [updatedAt] - When assignment was last updated
 * @property {Date} [syncedAt] - When assignment was synced to ElevenLabs
 * @property {string} [error] - Error message if status is 'failed'
 * @property {Object} [metadata] - Additional assignment metadata
 */

/**
 * @typedef {Object} SyncStatus
 * @property {string} fileId - File ID
 * @property {string} parrotId - Parrot ID
 * @property {'idle'|'queued'|'processing'|'completed'|'failed'} status - Sync status
 * @property {number} [progress] - Progress percentage (0-100)
 * @property {string} [message] - Status message
 * @property {string} [error] - Error message if failed
 * @property {Date} [startedAt] - When sync started
 * @property {Date} [completedAt] - When sync completed
 * @property {number} [retryCount] - Number of retry attempts
 * @property {Date} [nextRetryAt] - When next retry will occur
 */

/**
 * @typedef {Object} AssignmentConflict
 * @property {string} id - Conflict ID
 * @property {'duplicate_assignment'|'resource_limit'|'permission_denied'|'sync_conflict'} type - Conflict type
 * @property {string} fileId - File ID involved in conflict
 * @property {string} parrotId - Parrot ID involved in conflict
 * @property {string} message - Human-readable conflict description
 * @property {Object} details - Additional conflict details
 * @property {'auto_resolve'|'user_choice'|'admin_required'} resolutionStrategy - How to resolve
 * @property {string[]} [resolutionOptions] - Available resolution options
 * @property {string} [suggestedResolution] - Suggested resolution
 */

/**
 * @typedef {Object} AssignmentEvent
 * @property {string} type - Event type
 * @property {string} fileId - File ID
 * @property {string} parrotId - Parrot ID
 * @property {*} payload - Event payload
 * @property {Date} timestamp - Event timestamp
 * @property {string} userId - User who triggered the event
 */

/**
 * @typedef {Object} BatchAssignmentOperation
 * @property {string} id - Operation ID
 * @property {'assign'|'unassign'|'reassign'} operation - Operation type
 * @property {string[]} fileIds - File IDs to operate on
 * @property {string[]} parrotIds - Parrot IDs to operate on
 * @property {'pending'|'processing'|'completed'|'failed'|'cancelled'} status - Operation status
 * @property {number} progress - Progress percentage (0-100)
 * @property {AssignmentConflict[]} conflicts - Detected conflicts
 * @property {Assignment[]} results - Operation results
 * @property {Date} createdAt - When operation was created
 * @property {Date} [completedAt] - When operation completed
 */

/**
 * @typedef {Object} AssignmentFilter
 * @property {string[]} [fileIds] - Filter by file IDs
 * @property {string[]} [parrotIds] - Filter by parrot IDs
 * @property {string[]} [statuses] - Filter by assignment statuses
 * @property {Date} [createdAfter] - Filter by creation date
 * @property {Date} [createdBefore] - Filter by creation date
 * @property {boolean} [hasErrors] - Filter assignments with errors
 * @property {boolean} [needsSync] - Filter assignments needing sync
 */

/**
 * Default configuration for Knowledge Base Assignment Module
 * @type {Partial<KnowledgeBaseAssignmentConfig>}
 */
export const DEFAULT_KB_ASSIGNMENT_CONFIG = {
  mode: 'multi-select',
  securityLevel: 'user',
  showSyncStatus: true,
  allowBatchOperations: true,
  enableRealTimeUpdates: false,
  showConflictResolution: true,
  enableAssignmentHistory: false
};

/**
 * Context-specific default configurations
 * @type {Object<string, Partial<KnowledgeBaseAssignmentConfig>>}
 */
export const CONTEXT_CONFIGS = {
  'user-kb-dashboard': {
    context: 'user-kb-dashboard',
    mode: 'multi-select',
    securityLevel: 'user',
    allowBatchOperations: true,
    enableAssignmentHistory: false
  },
  'parrot-creation': {
    context: 'parrot-creation',
    mode: 'multi-select',
    securityLevel: 'user',
    allowBatchOperations: false,
    showSyncStatus: false,
    enableRealTimeUpdates: false
  },
  'parrot-editing': {
    context: 'parrot-editing',
    mode: 'multi-select',
    securityLevel: 'user',
    allowBatchOperations: true,
    showSyncStatus: true,
    enableRealTimeUpdates: false
  },
  'admin-kb': {
    context: 'admin-kb',
    mode: 'batch-assign',
    securityLevel: 'admin',
    allowBatchOperations: true,
    enableAssignmentHistory: true,
    showConflictResolution: true
  }
};

/**
 * Assignment status display configurations
 * @type {Object<string, Object>}
 */
export const ASSIGNMENT_STATUS_CONFIG = {
  pending: {
    label: 'Pending',
    color: 'yellow',
    icon: '⏳',
    description: 'Assignment is pending processing'
  },
  assigned: {
    label: 'Assigned',
    color: 'blue',
    icon: '📎',
    description: 'File is assigned but not yet synced'
  },
  syncing: {
    label: 'Syncing',
    color: 'blue',
    icon: '🔄',
    description: 'Assignment is being synced to ElevenLabs'
  },
  synced: {
    label: 'Synced',
    color: 'green',
    icon: '✅',
    description: 'Assignment is synced and active'
  },
  failed: {
    label: 'Failed',
    color: 'red',
    icon: '❌',
    description: 'Assignment failed to sync'
  },
  removed: {
    label: 'Removed',
    color: 'gray',
    icon: '🗑️',
    description: 'Assignment has been removed'
  }
};

/**
 * Sync status display configurations
 * @type {Object<string, Object>}
 */
export const SYNC_STATUS_CONFIG = {
  idle: {
    label: 'Idle',
    color: 'gray',
    icon: '⚪',
    description: 'No sync operation in progress'
  },
  queued: {
    label: 'Queued',
    color: 'yellow',
    icon: '⏳',
    description: 'Sync operation is queued'
  },
  processing: {
    label: 'Processing',
    color: 'blue',
    icon: '🔄',
    description: 'Sync operation is in progress'
  },
  completed: {
    label: 'Completed',
    color: 'green',
    icon: '✅',
    description: 'Sync operation completed successfully'
  },
  failed: {
    label: 'Failed',
    color: 'red',
    icon: '❌',
    description: 'Sync operation failed'
  }
};

/**
 * Validate Knowledge Base Assignment configuration
 * 
 * @param {KnowledgeBaseAssignmentConfig} config - Configuration to validate
 * @returns {Object} Validation result with valid flag and errors array
 */
export function validateKnowledgeBaseAssignmentConfig(config) {
  const errors = [];

  // Required fields
  if (!config) {
    errors.push('Configuration is required');
    return { valid: false, errors };
  }

  if (!config.context) {
    errors.push('Context is required');
  } else if (!['user-kb-dashboard', 'parrot-creation', 'parrot-editing', 'admin-kb'].includes(config.context)) {
    errors.push('Context must be one of: user-kb-dashboard, parrot-creation, parrot-editing, admin-kb');
  }

  if (!config.mode) {
    errors.push('Mode is required');
  } else if (!['single-select', 'multi-select', 'batch-assign'].includes(config.mode)) {
    errors.push('Mode must be one of: single-select, multi-select, batch-assign');
  }

  if (!config.securityLevel) {
    errors.push('Security level is required');
  } else if (!['user', 'admin'].includes(config.securityLevel)) {
    errors.push('Security level must be either "user" or "admin"');
  }

  // Validate callback functions if provided
  if (config.onAssignmentChange && typeof config.onAssignmentChange !== 'function') {
    errors.push('onAssignmentChange must be a function');
  }

  if (config.onSyncStatusUpdate && typeof config.onSyncStatusUpdate !== 'function') {
    errors.push('onSyncStatusUpdate must be a function');
  }

  if (config.onConflictDetected && typeof config.onConflictDetected !== 'function') {
    errors.push('onConflictDetected must be a function');
  }

  if (config.onAssignmentEvent && typeof config.onAssignmentEvent !== 'function') {
    errors.push('onAssignmentEvent must be a function');
  }

  // Context-specific validations
  if (config.context === 'admin-kb' && config.securityLevel !== 'admin') {
    errors.push('Admin knowledge base context requires admin security level');
  }

  if (config.mode === 'batch-assign' && config.allowBatchOperations === false) {
    errors.push('Batch assign mode requires allowBatchOperations to be true');
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Get default configuration for a specific context
 * 
 * @param {string} context - Context to get configuration for
 * @returns {KnowledgeBaseAssignmentConfig} Default configuration
 */
export function getDefaultConfigForContext(context) {
  const contextConfig = CONTEXT_CONFIGS[context] || {};
  return {
    ...DEFAULT_KB_ASSIGNMENT_CONFIG,
    ...contextConfig
  };
}

/**
 * Merge user configuration with defaults
 * 
 * @param {Partial<KnowledgeBaseAssignmentConfig>} userConfig - User-provided configuration
 * @param {string} [context] - Context to use for defaults
 * @returns {KnowledgeBaseAssignmentConfig} Merged configuration
 */
export function mergeWithDefaults(userConfig, context) {
  const defaults = context 
    ? getDefaultConfigForContext(context)
    : DEFAULT_KB_ASSIGNMENT_CONFIG;
    
  return {
    ...defaults,
    ...userConfig
  };
}
